import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { ResponseModule } from 'src/app/models/response.model';
import { EncryptionService } from '../encryption/encryption.service';
import { AdminUserModule } from 'src/app/models/admin-user.model';

const API_ENDPOINT_URL = environment.endpoint;

export const TOKEN_NAME = 'aToken';
export const REFRESH_TOKEN_NAME = 'rToken';

/**
 * Estructura utilizada para enviar las cabeceras en las peticiones.
 * @param headers - Cabeceras para peticiones API Rest
 */
interface HttpOptions {
  headers: HttpHeaders;
  withCredentials: boolean;
}

/**
 * Base para creación de servicios API REST
 */
@Injectable({
  providedIn: 'root',
})
export class ApiRestService {
  private httpOptions: HttpOptions;
  private router: Router;

  /**
   * Construcción de métodos y librerías necesarias para consumir servicios API Rest.
   * @param http Cliente Http para peticiones.
   * @param routerC Acceso al router de la aplicación para ajustes con parametros ó redirecciones.
   * @param encryptS Servicio para protección y encriptado de los datos durante el uso de ellos en la aplicación.
   */
  constructor(
    private http: HttpClient,
    private routerC: Router,
    private encryptS: EncryptionService
  ) {
    this.router = this.routerC;
  }

  getHttpOptions(): HttpOptions {
    return this.httpOptions;
  }

  getRouter() {
    return this.router;
  }

  /**
   * Obtener la ruta completa para consumir un Web Service.
   * @param path Ruta relativa del servicio a consultar.
   * @returns Devuelve la url completa del servicio.
   */
  getEndpointRoute(path: string): string {
    return API_ENDPOINT_URL + path;
  }

  /**
   * Obtener una instancia del servicio de encriptado de la aplicación.
   * @returns Devuelve una instancia del servicio de encriptado.
   */
  encryptService(): EncryptionService {
    return this.encryptS;
  }

  /**
   * Asignación de cabeceras para peticiones al servidor.
   */
  setHeaders(multipart = false) {
    let openHeaders = new HttpHeaders();
    if (multipart) {
      // openHeaders = openHeaders.append('Content-Type', 'multipart/form-data');
    } else {
      openHeaders = openHeaders.append('Content-Type', 'application/json');
    }
    openHeaders = openHeaders.append('Access-Control-Allow-Origin', '*');
    if (this.getToken() !== null) {
      openHeaders = openHeaders.append(
        'Authorization',
        'Bearer ' + this.getToken()
      );
      openHeaders = openHeaders.append('Origin_Platform', 'WebbApp');
    }
    this.httpOptions = {
      headers: openHeaders,
      withCredentials: true,
    };
  }

  /**
   * Validaciones de credenciales y adaptación de respuesta por parte del servidor.
   * @param res Respuesta obtenida por el servicio consumido.
   * @returns Información devuelta por el servidor lista para uso en la aplicación
   */
  extractData(res: ResponseModule): ResponseModule {
    const body = res;
    if (res.statusCode === 4) {
      alert(res.statusMessage);
      localStorage.clear();
      this.router.navigate(['/']);
      return null;
    } else {
      return body;
    }
  }

  /**
   *
   * @param urlRequest
   */
  getRequest(urlRequest: string): Observable<ResponseModule> {
    this.setHeaders();
    return this.http
      .get(urlRequest, this.httpOptions)
      .pipe(map(this.extractData));
  }

  /**
   *
   * @param urlRequest
   * @param dataObject
   */
  postRequest(
    urlRequest: string,
    dataObject: object
  ): Observable<ResponseModule> {
    this.setHeaders();
    return this.postCustomRequest(urlRequest, dataObject, {
      ...this.httpOptions,
      withCredentials: true,
    });
  }

  /**
   *
   * @param urlRequest
   * @param dataObject
   */
  postCustomRequest(
    urlRequest: string,
    dataObject: object,
    httpOptions: object
  ): Observable<ResponseModule> {
    this.setHeaders();
    return this.http
      .post(urlRequest, dataObject, httpOptions)
      .pipe(map(this.extractData));
  }

  /**
   *
   * @param urlRequest
   * @param formData
   */
  postMultipartRequest(
    urlRequest: string,
    formData: FormData
  ): Observable<ResponseModule> {
    this.setHeaders(true);
    console.log(this.httpOptions);
    return this.http
      .post(urlRequest, formData, this.httpOptions)
      .pipe(map(this.extractData));
  }

  /**
   * Obtener el token JWT almacenado en la aplicación
   */
  getToken(): string {
    return localStorage.getItem(TOKEN_NAME);
  }

  /**
   * Obtener el token JWT almacenado en la aplicación
   */
  getRefreshToken(): string {
    return localStorage.getItem(REFRESH_TOKEN_NAME);
  }

  /**
   * Retorna los segundos que faltan para que el token caduque.
   * O un valor negativo si ya caducó.
   */
  timeToExpire(): number {
    const token = this.getToken();
    if (!token) {
      return -1;
    }
    const decoded = jwtDecode(token) as any;
    if (!decoded.exp) {
      // Si no hay 'exp', interpretamos que está caducado o infinito
      return -1;
    }
    const now = Math.floor(new Date().valueOf() / 1000);
    return decoded.exp - now; // segundos restantes
  }

  /**
   * Obtener los datos del token JWT para usos diversos
   */
  getDataToken(): AdminUserModule {
    const decoded = jwtDecode(localStorage.getItem(TOKEN_NAME)) as any;
    console.log(decoded);

    const adminUser = new AdminUserModule();
    adminUser.adminID = decoded.id;
    adminUser.adminName = decoded.name;
    adminUser.adminLastName = decoded.lastName;
    adminUser.adminEmail = decoded.adminEmail;
    adminUser.adminImgProfile = 'assets/images/user.png';
    adminUser.adminType = +decoded.sysRole;
    adminUser.adminStatus = +decoded.status;
    adminUser.adminCreated = new Date(decoded.created);
    return adminUser;
  }

  /**
   * Asignar token JWT para el funcionamiento de la aplicación.
   * @param token Token en formato JWT con los datos necesarios.
   */
  setToken(token: string): void {
    localStorage.setItem(TOKEN_NAME, token);
  }

  /**
   * Asignar token JWT para el funcionamiento de la aplicación.
   * @param token Token en formato JWT con los datos necesarios.
   */
  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_NAME, token);
  }

  updateToken(): void {
    this.setHeaders();
    this.postCustomRequest(
      this.getEndpointRoute('/v1/auth/admin/refresh-token'),
      { refreshToken: this.getRefreshToken() },
      {
        ...this.httpOptions,
        withCredentials: true,
      }
    ).subscribe({
      next: (response: ResponseModule) => {
        if (response.statusCode === 0) {
          this.setToken(response.resultset.jwtToken);
          this.setRefreshToken(response.resultset.refreshToken);
        } else {
          this.clearExpiredSesion();
          this.routerC.navigate(['/auth/login']);
        }
      },
      error: (error) => {
        this.clearExpiredSesion();
        this.routerC.navigate(['/auth/login']);
      },
    });
  }

  updateTokenAsObservable(): Observable<string> {
    this.setHeaders();
    return this.postCustomRequest(
      this.getEndpointRoute('/v1/auth/admin/refresh-token'),
      { refreshToken: this.getRefreshToken() },
      {
        ...this.httpOptions,
        withCredentials: true,
      }
    ).pipe(
      map((response: ResponseModule) => {
        if (response.statusCode === 0) {
          this.setToken(response.resultset.jwtToken);
          this.setRefreshToken(response.resultset.refreshToken);
          return response.resultset.jwtToken;
        } else {
          throw new Error('Token refresh failed');
        }
      })
    );
  }

  /**
   * Obtener la fecha de expiración del token para diversas funciones.
   * @param token Token en formato JWT con los datos necesarios.
   */
  getTokenExpirationDate(token: string): Date {
    const decoded = jwtDecode(token);
    if (decoded.exp === undefined) {
      return null;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  /**
   *  Validar si el token JWT ya no es válido.
   * @param token Token en formato JWT con los datos necesarios.
   */
  isTokenExpired(token?: string): boolean {
    if (!token) {
      token = this.getToken();
    }
    if (!token || token === undefined) {
      return true;
    }

    const date = this.getTokenExpirationDate(token);
    if (date === undefined) {
      return false;
    }

    return date.valueOf() < new Date().valueOf();
  }

  clearExpiredSesion() {
    localStorage.clear();
  }
}
